import type {
  BugEmailStatus,
  BugReportKind,
  BugReportStatus,
  BugSeverity,
  BugTranscriptStatus,
} from "../../../api/types";
import { Badge } from "../../../components/ui";

type Tone = "ok" | "warn" | "danger" | "neutral";

const TRANSCRIPT: Record<BugTranscriptStatus, { label: string; tone: Tone }> = {
  pending: { label: "Transcribing", tone: "neutral" },
  done: { label: "Transcribed", tone: "ok" },
  failed: { label: "Not transcribed", tone: "danger" },
  skipped: { label: "No audio", tone: "neutral" },
};

const EMAIL: Record<BugEmailStatus, { label: string; tone: Tone }> = {
  pending: { label: "Sending", tone: "neutral" },
  sent: { label: "Emailed", tone: "ok" },
  failed: { label: "Email failed", tone: "danger" },
  not_configured: { label: "Email not set up", tone: "warn" },
};

export function TranscriptBadge({ status }: { status: BugTranscriptStatus }) {
  const b = TRANSCRIPT[status];
  return <Badge tone={b.tone}>{b.label}</Badge>;
}

export function EmailBadge({ status }: { status: BugEmailStatus }) {
  const b = EMAIL[status];
  return <Badge tone={b.tone}>{b.label}</Badge>;
}

const SEVERITY: Record<BugSeverity, { label: string; tone: Tone }> = {
  low: { label: "Assistant, low", tone: "neutral" },
  medium: { label: "Assistant, medium", tone: "neutral" },
  high: { label: "Assistant, high", tone: "warn" },
  critical: { label: "Assistant, critical", tone: "danger" },
};

const KIND: Record<BugReportKind, string> = {
  bug: "Bug",
  feature: "Feature request",
  question: "Question",
};

/** An assistant report, with its severity (a bug's only). */
export function SourceBadge({
  severity,
  kind = null,
}: {
  severity: BugSeverity | null;
  kind?: BugReportKind | null;
}) {
  const b =
    severity && (kind ?? "bug") === "bug"
      ? SEVERITY[severity]
      : { label: "Assistant", tone: "neutral" as Tone };
  return <Badge tone={b.tone}>{b.label}</Badge>;
}

/** Bug, Feature request or Question, beside the source. */
export function KindBadge({ kind }: { kind: BugReportKind | null | undefined }) {
  return <Badge tone="neutral">{KIND[kind ?? "bug"]}</Badge>;
}

const STATUS: Record<BugReportStatus, { label: string; tone: Tone | "accent" }> = {
  submitted: { label: "Submitted", tone: "neutral" },
  emailed: { label: "Emailed", tone: "neutral" },
  fix_proposed: { label: "Fix proposed", tone: "accent" },
  awaiting_reply: { label: "Waiting for the reporter", tone: "warn" },
  fixed: { label: "Fixed", tone: "ok" },
  wont_fix: { label: "Won't fix", tone: "neutral" },
  not_a_bug: { label: "Not a bug", tone: "neutral" },
};

/** "Fixed in 0.2.14", "Fixed on the server", or the status. */
export function statusLabel(status: BugReportStatus, fixedVersion: string | null): string {
  if (status === "fixed" && fixedVersion) {
    return fixedVersion.startsWith("server") ? "Fixed on the server" : `Fixed in ${fixedVersion}`;
  }
  return STATUS[status]?.label ?? status;
}

export function StatusBadge({
  status,
  fixedVersion = null,
}: {
  status: BugReportStatus;
  fixedVersion?: string | null;
}) {
  return <Badge tone={STATUS[status]?.tone ?? "neutral"}>{statusLabel(status, fixedVersion)}</Badge>;
}

/** R-12. */
export const reportCode = (n: number | null | undefined) => (n ? `R-${n}` : "");

export const isResolved = (s: BugReportStatus) => s === "fixed" || s === "wont_fix" || s === "not_a_bug";
